import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { useCompare } from "@/context/CompareContext";
import { toast } from "@/lib/toast";
import Navbar from "@/components/Navbar";
import ListingCard from "@/components/ListingCard";
import BuyerPassModal, { openBuyerPass } from "@/components/BuyerPassModal";
import {
  CONDITIONS, LISTING_TYPES, MAG_RANGES, RETICLES, MOUNTS, US_STATES,
  TUBE_DIAMETERS, FOCAL_PLANES, COLORS, DEVICE_TYPES,
} from "@/lib/constants";
import {
  Search, SlidersHorizontal, X, RadioTower, Bookmark, BookmarkPlus, GitCompareArrows, Lock, Plus, Bell, ArrowUpRight,
} from "lucide-react";
import { partGroupOf, labelToGroup, PART_PARENTS } from "@/lib/partCategories";

// --- Feed category-first tabs (mirror of backend TAB_CATEGORIES) --------
const TABS = [
  { id: "optics", label: "Optics" },
  { id: "thermals", label: "Thermals" },
  { id: "night_vision", label: "Night Vision" },
  { id: "lasers", label: "Lasers" },
  { id: "mounts", label: "Mounts" },
  { id: "accessories", label: "Accessories" },
  { id: "knives", label: "Knives" },
  ...PART_PARENTS.map((p) => ({ id: p.id, label: p.label })),
  { id: "all", label: "All" },
];
const NV_THERMAL_CATEGORY = "Night Vision & Thermal Optics";
const LASERS_CATEGORY = "Lasers";
const KNIVES_CATEGORY = "Knives & Blades";
const TAB_CATEGORIES = {
  optics: ["Rifle Scopes", "Red Dots & Reflex Sights", "Prism Scopes & Magnifiers", "Rangefinders", "Binoculars & Spotting Scopes"],
  lasers: [LASERS_CATEGORY],
  mounts: ["Mounts, Rings & Bases"],
  accessories: ["Parts & Accessories"],
  knives: [KNIVES_CATEGORY],
};
function tabForCategory(cat) {
  if (!cat) return "";
  if (cat === NV_THERMAL_CATEGORY) return "night_vision";
  // A part leaf id (barrels.rifle) or a parent label ("Barrels") maps to its parent tab.
  const g = partGroupOf(cat) || labelToGroup(cat);
  if (g) return g;
  for (const [tab, cats] of Object.entries(TAB_CATEGORIES)) {
    if (cats.includes(cat)) return tab;
  }
  return "";
}

const _PART_PARENT_LABEL = Object.fromEntries(PART_PARENTS.map((p) => [p.id, p.label]));
const TAB_CREATE_CATEGORY = {
  thermals: NV_THERMAL_CATEGORY,
  night_vision: NV_THERMAL_CATEGORY,
  lasers: LASERS_CATEGORY,
  mounts: "Mounts, Rings & Bases",
  accessories: "Parts & Accessories",
  knives: KNIVES_CATEGORY,
};
// Top-level category to pre-select on /create for a given feed tab. Multi-category tabs ("optics")
// and "all" have no single category → null (open /create with its default).
function createCategoryForTab(tabId) {
  if (!tabId || tabId === "all" || tabId === "optics") return null;
  return TAB_CREATE_CATEGORY[tabId] || _PART_PARENT_LABEL[tabId] || null;
}

const SELLER_TYPES = [
  { id: "individual", label: "Individual" },
  { id: "dealer", label: "Dealer" },
];
const BADGE_GROUPS = [
  { title: "Verification", items: [
    { id: "verified_id", label: "ID Verified" },
    { id: "dealer", label: "Dealer Verified" },
  ] },
  { title: "Recognition", items: [
    { id: "founding_member", label: "Founding Member" },
    { id: "ambassador", label: "Ambassador" },
    { id: "beta_tester", label: "Beta Tester" },
  ] },
];
const SPECIALTIES = ["Hunting", "Competition", "Long Range", "Duty/Patrol", "Collector"];

const EMPTY = {
  tab: "all", category: "", listing_type: "", brand: "", condition: "", color: "", reticle_type: "",
  mount_type: "", mag_range: "", open_to_trades: false, min_price: "", max_price: "",
  state: "", search: "", sort: "newest",
  seller_type: "", tube_diameter: "", focal_plane: "", min_mag: "", max_mag: "", max_weight_oz: "",
  device_type: "", sensor_resolution: "", detection_min: "",
  specialty: "", badge: [],
};

const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-sm text-foreground transition-colors";
const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Brand monogram chip — deterministic colour + initials, so the brand rail scans fast without
// sourcing real (copyrighted) logos. Same brand always renders the same chip.
function brandColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 30%)`;
}
function brandInitials(name) {
  const words = String(name).replace(/[^A-Za-z0-9 &]/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return String(name).replace(/[^A-Za-z0-9]/g, "").slice(0, 2).toUpperCase() || "?";
}
function BrandMark({ name }) {
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-sm text-[9px] font-heading font-bold text-white/90 shrink-0"
      style={{ backgroundColor: brandColor(name) }}
      data-testid={`brand-mark-${slug(name)}`}
      aria-hidden
    >
      {brandInitials(name)}
    </span>
  );
}

// Hydrate the full filter set from URL search params so deep-linked / bookmarked / saved-search
// URLs (e.g. ?min_price=500&brand=Vortex) actually apply on load — mirrors buildQuery's encoding.
function filtersFromParams(params) {
  const f = { ...EMPTY };
  for (const k of Object.keys(EMPTY)) {
    if (k === "tab") continue; // derived below
    if (k === "badge") {
      const raw = params.get("badge");
      f.badge = raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];
      continue;
    }
    if (k === "open_to_trades") {
      f.open_to_trades = params.get("open_to_trades") === "true";
      continue;
    }
    const v = params.get(k);
    if (v != null) f[k] = v;
  }
  f.tab = params.get("tab") || tabForCategory(f.category) || "all";
  return f;
}

function CountPill({ n }) {
  if (n == null) return null;
  return <span className={`ml-auto font-mono text-[10px] tabular-nums ${n > 0 ? "text-muted-foreground" : "text-muted-foreground/40"}`}>{n}</span>;
}

function RailGroup({ label, children }) {
  return (
    <div className="mb-5">
      <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-2">{label}</h4>
      {children}
    </div>
  );
}

// A single count-annotated selectable row (checkbox / radio style).
function OptionRow({ testid, active, label, count, onClick, mark }) {
  // A zero-count option still RENDERS (so a member sees it exists) but is disabled with its count —
  // nothing matches it right now. An ACTIVE option stays clickable so it can always be cleared.
  const disabled = count === 0 && !active;
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm transition-colors border ${
        active
          ? "border-olive-bright bg-olive/10 text-foreground"
          : "border-transparent hover:border-sniper text-secondary-foreground hover:text-foreground"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      {mark || <span className={`inline-block w-3 h-3 border ${active ? "border-olive-bright bg-olive-bright" : "border-sniper"} shrink-0`} />}
      <span className="truncate">{label}</span>
      <CountPill n={count} />
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function Feed() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(() => filtersFromParams(params));
  const [listings, setListings] = useState([]);
  const [facets, setFacets] = useState(null);
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [live, setLive] = useState(true);
  const { user, businessFeaturesEnabled, buyerPassEnabled, refresh } = useAuth();
  // Sticky rail must clear the full header: main row (64) + the logged-in secondary icon row (44),
  // plus a small gap. (ADMIN MODE now shows only a 3px rail, so admins get the same offset.)
  const railTop = user ? 124 : 80;
  const compare = useCompare();
  const compareCount = compare?.items?.length || 0;
  const [saved, setSaved] = useState([]);
  const [savedMeta, setSavedMeta] = useState({ limit: 0, unlimited: true, count: 0, entitled: true, gate_message: null });
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const buildQuery = useCallback((f) => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => {
      if (k === "badge") { if (Array.isArray(v) && v.length) q.set("badge", v.join(",")); return; }
      if (k === "tab") { if (v && v !== "all") q.set("tab", v); return; }
      if (v === "" || v === false || v == null) return;
      q.set(k, v);
    });
    return q.toString();
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const qs = buildQuery(filtersRef.current);
      const url = `/listings?${qs}${silent ? "" : `${qs ? "&" : ""}facets=true`}`;
      const { data } = await api.get(url);
      setListings(data.listings);
      if (data.gate !== undefined) setGate(data.gate);
      if (!silent && data.facets) setFacets(data.facets);
    } catch {} finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => { load(); }, [load, filters]);

  // Return from the Buyer-Pass Stripe Checkout: confirm the pass server-side, refresh the session
  // so the feed ungates immediately, and clean the query string.
  useEffect(() => {
    const status = params.get("buyer_pass");
    if (!status) return;
    if (status === "success") {
      const sid = params.get("session_id");
      const finish = async () => {
        try { if (sid) await api.get(`/buyer-pass/status/${sid}`); } catch {}
        await refresh();
        toast.success("Buyer Pass active — the whole marketplace is unlocked for 7 days.");
        load();
      };
      finish();
    } else if (status === "cancel") {
      toast.info("Checkout canceled — no charge was made.");
    }
    const next = new URLSearchParams(params);
    next.delete("buyer_pass"); next.delete("session_id");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listingsRef = useRef([]);
  useEffect(() => { listingsRef.current = listings; }, [listings]);

  // First-party site-search tracking (debounced) — feeds admin Site Search analytics.
  useEffect(() => {
    const term = filters.search.trim();
    if (term.length < 2) return;
    const t = setTimeout(() => {
      const sid = sessionStorage.getItem("os_ssid") || localStorage.getItem("os_vid") || "anon";
      api.post("/analytics/search-beacon", { query: term, results_count: listingsRef.current.length, session_id: sid }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    if (!live) return;
    const t = setInterval(() => load(true), 6000);
    return () => clearInterval(t);
  }, [live, load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const setTab = (id) => {
    setFilters((f) => ({ ...f, tab: id, category: "" }));
    const next = new URLSearchParams(params);
    next.delete("category"); next.delete("tab");
    setParams(next, { replace: true });
  };
  const toggleBadge = (id) => setFilters((f) => {
    const cur = f.badge || [];
    return { ...f, badge: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const reset = () => { setFilters({ ...EMPTY }); setParams({}); };

  const loadSaved = useCallback(() => {
    if (!user) { setSaved([]); return; }
    api.get("/me/saved-searches").then(({ data }) => {
      setSaved(data.searches);
      setSavedMeta({ limit: data.limit, unlimited: data.unlimited, count: data.count, entitled: data.entitled !== false, gate_message: data.gate_message });
    }).catch(() => {});
  }, [user]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const applySaved = (s) => {
    setFilters({ ...EMPTY, ...s.filters, badge: s.filters.badge || [], tab: s.filters.tab || "all", sort: s.filters.sort || "newest" });
    setShowFilters(false);
  };

  const saveCurrent = async () => {
    const name = saveName.trim();
    if (!name) { toast.error("Give your search a name."); return; }
    try {
      const { data } = await api.post("/me/saved-searches", { name, filters: { ...filters, badge: filters.badge.join(",") } });
      toast.success(`Saved "${name}".`);
      if (data.broad) toast.warning(data.broad_hint);
      setSaveOpen(false); setSaveName(""); loadSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteSaved = async (id) => {
    try { await api.delete(`/me/saved-searches/${id}`); loadSaved(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // --- active-filter chips ------------------------------------------------
  const chips = useMemo(() => {
    const out = [];
    if (filters.category) out.push({ key: "category", label: filters.category });
    if (filters.seller_type) out.push({ key: "seller_type", label: SELLER_TYPES.find((s) => s.id === filters.seller_type)?.label || filters.seller_type });
    (filters.badge || []).forEach((b) => {
      const meta = BADGE_GROUPS.flatMap((g) => g.items).find((i) => i.id === b);
      out.push({ key: `badge:${b}`, label: meta?.label || b });
    });
    if (filters.condition) out.push({ key: "condition", label: filters.condition });
    if (filters.color) out.push({ key: "color", label: filters.color });
    if (filters.listing_type) out.push({ key: "listing_type", label: LISTING_TYPES.find((t) => t.value === filters.listing_type)?.label || filters.listing_type });
    if (filters.specialty) out.push({ key: "specialty", label: filters.specialty });
    if (filters.reticle_type) out.push({ key: "reticle_type", label: `Reticle: ${filters.reticle_type}` });
    if (filters.mount_type) out.push({ key: "mount_type", label: filters.mount_type });
    if (filters.tube_diameter) out.push({ key: "tube_diameter", label: filters.tube_diameter });
    if (filters.focal_plane) out.push({ key: "focal_plane", label: filters.focal_plane });
    if (filters.device_type) out.push({ key: "device_type", label: filters.device_type });
    if (filters.sensor_resolution) out.push({ key: "sensor_resolution", label: `Sensor: ${filters.sensor_resolution}` });
    if (filters.detection_min) out.push({ key: "detection_min", label: `≥ ${filters.detection_min} yd` });
    if (filters.mag_range) out.push({ key: "mag_range", label: filters.mag_range });
    if (filters.min_mag || filters.max_mag) out.push({ key: "mag", label: `${filters.min_mag || "0"}–${filters.max_mag || "∞"}×` });
    if (filters.max_weight_oz) out.push({ key: "max_weight_oz", label: `≤ ${filters.max_weight_oz} oz` });
    if (filters.min_price || filters.max_price) out.push({ key: "price", label: `$${filters.min_price || "0"}–$${filters.max_price || "∞"}` });
    if (filters.state) out.push({ key: "state", label: filters.state });
    if (filters.open_to_trades) out.push({ key: "open_to_trades", label: "Open to trade" });
    return out;
  }, [filters]);

  const removeChip = (key) => {
    if (key.startsWith("badge:")) { toggleBadge(key.split(":")[1]); return; }
    if (key === "mag") { setFilters((f) => ({ ...f, min_mag: "", max_mag: "" })); return; }
    if (key === "price") { setFilters((f) => ({ ...f, min_price: "", max_price: "" })); return; }
    set(key, key === "open_to_trades" ? false : "");
  };

  const tabCount = (id) => facets?.tabs?.[id];

  // Two DIFFERENT empty facts, told apart by a filter-independent server count (total_active):
  //   • marketplace empty  → no active listing exists at all → nothing to filter. The rail, tabs,
  //     save-search and chips DON'T render; the one action is "post a listing".
  //   • filter returns zero → listings exist but this filter matched none → keep the rail + a
  //     "clear filters" action.
  const marketplaceEmpty = listings.length === 0 && facets != null && (facets.total_active || 0) === 0;

  // Empty-CATEGORY state (distinct from the generic "no match"): a real category tab is selected
  // with NO other filters/search, yet nothing is listed there → the CATEGORY itself is empty
  // (a freshly-launched category), not a filter that matched zero. Invite the first listing
  // instead of offering "clear filters". Filter-produced zeros keep the existing feed-empty box.
  const tabLabel = TABS.find((t) => t.id === filters.tab)?.label || "";
  const isCategoryTab = !!filters.tab && filters.tab !== "all";
  const noNarrowing = chips.length === 0 && !filters.search.trim();
  const categoryEmpty = isCategoryTab && noNarrowing;
  const createCat = createCategoryForTab(filters.tab);
  const alreadyNotified = saved.some((s) => s.filters && s.filters.tab === filters.tab);

  const notifyCategory = async () => {
    if (!user) { navigate("/join"); return; }
    if (!savedMeta.entitled) { navigate("/pricing"); return; }
    if (alreadyNotified) { toast.success(`You're set — new ${tabLabel} will appear in your saved searches.`); return; }
    try {
      await api.post("/me/saved-searches", { name: `New ${tabLabel}`, filters: { tab: filters.tab } });
      toast.success(`Done — new ${tabLabel} listings will show in your saved searches.`);
      loadSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  // Business/dealer is dark for the individuals-only launch — drop the Dealer seller facet
  // and the "Dealer Verified" badge from the discovery filters.
  const badgeGroups = businessFeaturesEnabled
    ? BADGE_GROUPS
    : BADGE_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.id !== "dealer") }));

  // --- the filter rail (shared desktop + mobile) --------------------------
  const rail = (
    <div className="rail min-w-0 overflow-hidden border border-sniper bg-surface p-4">
      {businessFeaturesEnabled && (
        <RailGroup label="Seller">
          <div className="space-y-1">
            {SELLER_TYPES.map((s) => (
              <OptionRow
                key={s.id}
                testid={`filter-seller-${s.id}`}
                active={filters.seller_type === s.id}
                label={s.label}
                count={facets?.seller_type?.[s.id]}
                onClick={() => set("seller_type", filters.seller_type === s.id ? "" : s.id)}
              />
            ))}
          </div>
        </RailGroup>
      )}

      {badgeGroups.map((g) => (
        <RailGroup key={g.title} label={`Badges · ${g.title}`}>
          <div className="space-y-1">
            {g.items.map((b) => (
              <OptionRow
                key={b.id}
                testid={`filter-badge-${b.id}`}
                active={(filters.badge || []).includes(b.id)}
                label={b.label}
                count={facets?.badges?.[b.id]}
                onClick={() => toggleBadge(b.id)}
              />
            ))}
          </div>
        </RailGroup>
      ))}

      <RailGroup label="Listing Type">
        <div className="space-y-1">
          {LISTING_TYPES.map((t) => (
            <OptionRow
              key={t.value}
              testid={`filter-listing-type-${t.value}`}
              active={filters.listing_type === t.value}
              label={t.label}
              count={facets?.listing_type?.[t.value]}
              onClick={() => set("listing_type", filters.listing_type === t.value ? "" : t.value)}
            />
          ))}
        </div>
      </RailGroup>

      <RailGroup label="Condition">
        <div className="space-y-1">
          {CONDITIONS.map((c) => (
            <OptionRow
              key={c}
              testid={`filter-condition-${slug(c)}`}
              active={filters.condition === c}
              label={c}
              count={facets?.condition?.[c]}
              onClick={() => set("condition", filters.condition === c ? "" : c)}
            />
          ))}
        </div>
      </RailGroup>

      <RailGroup label="Color / finish">
        <div className="space-y-1">
          {COLORS.map((c) => (
            <OptionRow
              key={c}
              testid={`filter-color-${slug(c)}`}
              active={filters.color === c}
              label={c}
              count={facets?.color?.[c]}
              onClick={() => set("color", filters.color === c ? "" : c)}
            />
          ))}
        </div>
      </RailGroup>

      {(filters.tab === "thermals" || filters.tab === "night_vision" || (facets?.device_type && Object.keys(facets.device_type).length > 0)) && (
        <>
          <RailGroup label="Device type">
            <div className="space-y-1" data-testid="filter-device-type-group">
              {DEVICE_TYPES.map((d) => (
                <OptionRow
                  key={d}
                  testid={`filter-device-type-${slug(d)}`}
                  active={filters.device_type === d}
                  label={d}
                  count={facets?.device_type?.[d]}
                  onClick={() => set("device_type", filters.device_type === d ? "" : d)}
                />
              ))}
            </div>
          </RailGroup>

          {facets?.sensor_resolution && Object.keys(facets.sensor_resolution).length > 0 && (
            <RailGroup label="Sensor resolution">
              <div className="space-y-1" data-testid="filter-sensor-resolution-group">
                {Object.entries(facets.sensor_resolution).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                  <OptionRow
                    key={s}
                    testid={`filter-sensor-resolution-${slug(s)}`}
                    active={filters.sensor_resolution === s}
                    label={s}
                    count={n}
                    onClick={() => set("sensor_resolution", filters.sensor_resolution === s ? "" : s)}
                  />
                ))}
              </div>
            </RailGroup>
          )}

          <RailGroup label="Detection range">
            <Field label="Min detection range (yd)">
              <input data-testid="filter-detection-min" type="number" min="0" step="10" value={filters.detection_min} onChange={(e) => set("detection_min", e.target.value)} placeholder="e.g. 1500" className={inputCls} />
            </Field>
          </RailGroup>
        </>
      )}

      {facets?.brand && Object.keys(facets.brand).length > 0 && (
        <RailGroup label="Brand">
          <div className="space-y-1 max-h-56 overflow-auto pr-1" data-testid="filter-brand-group">
            {Object.entries(facets.brand).sort((a, b) => b[1] - a[1]).map(([b, n]) => (
              <div key={b} className="flex items-center gap-1">
                <div className="flex-1 min-w-0">
                  <OptionRow
                    testid={`filter-brand-${slug(b)}`}
                    active={filters.brand === b}
                    label={b}
                    count={n}
                    mark={<BrandMark name={b} />}
                    onClick={() => set("brand", filters.brand === b ? "" : b)}
                  />
                </div>
                <Link to={`/brand/${slug(b)}`} title={`View the ${b} brand page`}
                  data-testid={`brand-page-link-${slug(b)}`}
                  className="shrink-0 p-1.5 text-muted-foreground hover:text-olive-bright transition-colors">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </RailGroup>
      )}


      <div className="border-t border-sniper/60 pt-4">
        <h4 className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-3">More filters</h4>
        <Field label="Specialty (Shoots)">
          <select data-testid="filter-specialty" value={filters.specialty} onChange={(e) => set("specialty", e.target.value)} className={inputCls}>
            <option value="">Any specialty</option>
            {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Magnification">
          <select data-testid="filter-mag" value={filters.mag_range} onChange={(e) => set("mag_range", e.target.value)} className={inputCls}>
            <option value="">Any</option>
            {MAG_RANGES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Reticle">
          <select data-testid="filter-reticle" value={filters.reticle_type} onChange={(e) => set("reticle_type", e.target.value)} className={inputCls}>
            <option value="">Any</option>
            {RETICLES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Mount">
          <select data-testid="filter-mount" value={filters.mount_type} onChange={(e) => set("mount_type", e.target.value)} className={inputCls}>
            <option value="">Any</option>
            {MOUNTS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Tube Diameter">
          <select data-testid="filter-tube" value={filters.tube_diameter} onChange={(e) => set("tube_diameter", e.target.value)} className={inputCls}>
            <option value="">Any</option>
            {TUBE_DIAMETERS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Focal Plane">
          <select data-testid="filter-focal-plane" value={filters.focal_plane} onChange={(e) => set("focal_plane", e.target.value)} className={inputCls}>
            <option value="">Any</option>
            {FOCAL_PLANES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Magnification (×)">
          <div className="range flex gap-2 w-full">
            <input data-testid="filter-min-mag" type="number" min="0" step="0.1" value={filters.min_mag} onChange={(e) => set("min_mag", e.target.value)} placeholder="Min" className={`${inputCls} flex-1 min-w-0`} />
            <input data-testid="filter-max-mag" type="number" min="0" step="0.1" value={filters.max_mag} onChange={(e) => set("max_mag", e.target.value)} placeholder="Max" className={`${inputCls} flex-1 min-w-0`} />
          </div>
        </Field>
        <Field label="Max Weight (oz)">
          <input data-testid="filter-max-weight" type="number" min="0" step="0.1" value={filters.max_weight_oz} onChange={(e) => set("max_weight_oz", e.target.value)} placeholder="e.g. 30" className={inputCls} />
        </Field>
        <Field label="Price Range ($)">
          <div className="range flex gap-2 w-full">
            <input data-testid="filter-min-price" type="number" min="0" value={filters.min_price} onChange={(e) => set("min_price", e.target.value)} placeholder="Min" className={`${inputCls} flex-1 min-w-0`} />
            <input data-testid="filter-max-price" type="number" min="0" value={filters.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder="Max" className={`${inputCls} flex-1 min-w-0`} />
          </div>
        </Field>
        <Field label="State">
          <select data-testid="filter-state" value={filters.state} onChange={(e) => set("state", e.target.value)} className={inputCls}>
            <option value="">Anywhere</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 mt-1 cursor-pointer" data-testid="filter-trades">
          <input type="checkbox" checked={filters.open_to_trades} onChange={(e) => set("open_to_trades", e.target.checked)} className="accent-olive w-4 h-4" />
          <span className="font-mono text-xs uppercase text-secondary-foreground">Open to trade only</span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        {marketplaceEmpty ? (
          <div className="border border-sniper bg-surface p-16 text-center" data-testid="feed-empty-marketplace">
            <p className="font-heading text-2xl uppercase text-secondary-foreground">The marketplace is empty</p>
            <p className="text-sm text-muted-foreground mt-2 font-body">No optics are listed yet — be the first to post one.</p>
            <Link to={user ? "/create" : "/register"} data-testid="feed-empty-post-btn"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-6 py-3 transition-colors">
              Post a listing
            </Link>
          </div>
        ) : (
        <>
        {/* top bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              data-testid="feed-search"
              value={filters.search}
              onChange={(e) => set("search", e.target.value)}
              placeholder="Search scopes, brands, models..."
              className="w-full bg-surface border border-sniper focus:border-olive outline-none pl-10 pr-3 py-2.5 text-foreground transition-colors font-body"
            />
          </div>
          <select data-testid="feed-sort" value={filters.sort} onChange={(e) => set("sort", e.target.value)}
            className="bg-surface border border-sniper focus:border-olive outline-none px-3 py-2.5 text-sm font-mono uppercase text-foreground">
            <option value="newest">Newest</option>
            <option value="price_low">Price ↑</option>
            <option value="price_high">Price ↓</option>
          </select>
          <button data-testid="feed-filter-toggle" onClick={() => setShowFilters(!showFilters)}
            className="lg:hidden inline-flex items-center gap-2 border border-sniper px-4 py-2.5 font-mono uppercase text-sm">
            <SlidersHorizontal className="w-4 h-4" /> Filters {chips.length > 0 && `(${chips.length})`}
          </button>
        </div>

        {/* WANTED/ISO MODE — a first-class toggle, not a category. Surfaces the ISO board
            (listing_type=buy): members looking to BUY or TRADE FOR an item. It's a mode, so it
            sits apart from the category tabs. */}
        <div className="flex items-center gap-2 mb-3" data-testid="feed-mode-toggle">
          <button
            data-testid="feed-mode-marketplace"
            onClick={() => set("listing_type", "")}
            className={`font-heading uppercase text-sm tracking-tight px-4 py-2 border transition-colors ${filters.listing_type !== "buy" ? "border-olive-bright text-olive-bright bg-olive/10" : "border-sniper text-secondary-foreground hover:text-foreground"}`}
          >
            Marketplace
          </button>
          <button
            data-testid="feed-mode-wanted"
            title="ISO — In Search Of: members looking to buy or trade for an item."
            onClick={() => set("listing_type", "buy")}
            className={`font-heading uppercase text-sm tracking-tight px-4 py-2 border transition-colors flex items-center gap-2 ${filters.listing_type === "buy" ? "border-volt text-volt bg-volt/10" : "border-sniper text-secondary-foreground hover:text-foreground"}`}
          >
            <RadioTower className="w-4 h-4" /> Wanted/ISO
            {facets?.listing_type?.buy != null && (
              <span className="font-mono text-[10px] tabular-nums">{facets.listing_type.buy}</span>
            )}
          </button>
        </div>

        {/* category tab bar */}
        <div className="border-b border-sniper mb-4 overflow-x-auto" data-testid="feed-tabs">
          <div className="flex items-stretch gap-1 min-w-max">
            {TABS.map((t) => {
              const active = filters.tab === t.id;
              const c = tabCount(t.id);
              return (
                <button
                  key={t.id}
                  data-testid={`feed-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`relative px-4 py-2.5 font-heading uppercase text-sm tracking-tight whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
                    active ? "border-olive-bright text-olive-bright" : "border-transparent text-secondary-foreground hover:text-foreground"
                  }`}
                >
                  {t.label}
                  {c != null && (
                    <span className={`font-mono text-[10px] tabular-nums px-1.5 py-0.5 ${active ? "bg-olive-bright/15 text-olive-bright" : "bg-surface-2 text-muted-foreground"}`}>{c}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {user && (
          <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="saved-searches-bar">
            <span className="font-mono text-[10px] uppercase text-muted-foreground flex items-center gap-1"><Bookmark className="w-3.5 h-3.5" /> Saved:</span>
            {saved.map((s) => (
              <span key={s.search_id} className="inline-flex items-center gap-1 border border-sniper hover:border-olive bg-surface pl-2.5 pr-1 py-1 group transition-colors" data-testid={`saved-chip-${s.search_id}`}>
                <button onClick={() => applySaved(s)} data-testid={`apply-saved-${s.search_id}`} className="font-mono text-[11px] uppercase text-secondary-foreground group-hover:text-foreground">{s.name}</button>
                <button onClick={() => deleteSaved(s.search_id)} data-testid={`delete-saved-${s.search_id}`} className="text-muted-foreground hover:text-blaze"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {saved.length === 0 && <span className="font-mono text-[10px] text-muted-foreground/60 lowercase">none yet — set your filters, then save</span>}
            {savedMeta.entitled ? (
              <button onClick={() => setSaveOpen(true)} data-testid="save-search-btn" className="inline-flex items-center gap-1 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono text-[11px] uppercase px-2.5 py-1 transition-colors">
                <BookmarkPlus className="w-3.5 h-3.5" /> Save this search
              </button>
            ) : (
              <Link to="/pricing" data-testid="save-search-gate" className="inline-flex items-center gap-1 border border-sniper text-muted-foreground hover:text-foreground font-mono text-[11px] uppercase px-2.5 py-1 transition-colors">
                <Lock className="w-3 h-3" /> Saved searches — upgrade
              </Link>
            )}
          </div>
        )}

        {/* active filter chips */}
        {chips.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="feed-active-chips">
            {chips.map((c) => (
              <button key={c.key} data-testid={`chip-${slug(c.key)}`} onClick={() => removeChip(c.key)}
                className="inline-flex items-center gap-1.5 bg-olive/10 border border-olive/40 text-olive-bright font-mono text-[11px] uppercase px-2.5 py-1 hover:bg-olive/20 transition-colors">
                {c.label} <X className="w-3 h-3" />
              </button>
            ))}
            <button onClick={reset} data-testid="feed-clear-all" className="font-mono text-[10px] uppercase text-blaze hover:underline">Clear all</button>
          </div>
        )}

        <div className="page grid grid-cols-1 lg:grid-cols-[236px_minmax(0,1fr)] gap-6">
          {/* filter rail */}
          <aside className={`${showFilters ? "block" : "hidden"} lg:block`} data-testid="feed-filters">
            <div className="lg:sticky" style={{ top: railTop }}>
              <div className="flex items-center justify-between h-9 mb-3">
                <h3 className="font-heading text-lg uppercase tracking-tight leading-none">Filters</h3>
                {chips.length > 0 && (
                  <button onClick={reset} data-testid="feed-reset" className="text-[11px] font-mono uppercase text-blaze hover:underline flex items-center gap-1">
                    <X className="w-3 h-3" /> Reset
                  </button>
                )}
              </div>
              {rail}
            </div>
          </aside>

          {/* results */}
          <main className="min-w-0">
            <div className="flex items-center justify-between h-9 mb-3">
              <div className="flex items-center gap-3 text-xs font-mono uppercase text-secondary-foreground leading-none">
                <span data-testid="feed-count">{listings.length} {listings.length === 1 ? "listing" : "listings"}</span>
                {compareCount > 0 && (
                  <Link to="/compare" data-testid="feed-compare-link"
                    className="inline-flex items-center gap-1.5 px-2 py-1 border border-olive-bright text-olive-bright hover:bg-olive-bright/10 transition-colors">
                    <GitCompareArrows className="w-3.5 h-3.5" /> Compare
                    <span className="bg-olive-bright text-black text-[9px] font-bold px-1 min-w-[15px] text-center leading-4">{compareCount}</span>
                  </Link>
                )}
              </div>
              <button onClick={() => setLive(!live)} data-testid="feed-live-toggle"
                className={`flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide px-2 py-1 border ${live ? "border-blaze text-blaze animate-pulse-border" : "border-sniper text-muted-foreground"}`}>
                <RadioTower className="w-3.5 h-3.5" /> Live {live ? "on" : "off"}
              </button>
            </div>
            {buyerPassEnabled && gate?.active && !loading && listings.length > (gate.free_count || 5) && (
              <div className="mb-4 border border-volt/40 bg-volt/[0.06] p-4 flex items-center justify-between gap-4 flex-wrap" data-testid="feed-gate-banner">
                <div className="flex items-start gap-3 min-w-0">
                  <Lock className="w-5 h-5 text-volt shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="font-heading font-bold uppercase tracking-tight text-foreground text-base">You're browsing {gate.free_count || 5} of many</p>
                    <p className="text-[13px] font-body text-secondary-foreground mt-0.5">Unlock every listing — full prices and sellers — with a 7-day Buyer Pass. $5, one-time.</p>
                  </div>
                </div>
                <button onClick={openBuyerPass} data-testid="feed-gate-unlock-btn"
                  className="shrink-0 inline-flex items-center gap-2 bg-volt hover:brightness-110 text-black font-heading font-bold uppercase text-sm px-5 py-2.5 transition-all">
                  <Lock className="w-4 h-4" /> Get Buyer Pass
                </button>
              </div>
            )}
            {loading ? (
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="border border-sniper bg-surface animate-pulse">
                    <div className="aspect-square bg-surface-2" />
                    <div className="p-3 space-y-2"><div className="h-5 bg-surface-2 w-1/2" /><div className="h-3 bg-surface-2 w-full" /></div>
                  </div>
                ))}
              </div>
            ) : listings.length === 0 ? (
              categoryEmpty ? (
                <div className="border border-sniper bg-surface p-12 sm:p-16 text-center" data-testid="feed-empty-category">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-olive-bright" data-testid="feed-empty-category-kicker">{tabLabel} · New Category</p>
                  <p className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-foreground mt-3" data-testid="feed-empty-category-headline">Be the first to list here</p>
                  <p className="text-sm text-muted-foreground mt-2 font-body max-w-md mx-auto" data-testid="feed-empty-category-sub">
                    No {tabLabel} listings yet — this category just launched. Post yours and it'll be the first thing buyers see when they check {tabLabel}.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
                    <Link to={createCat ? `/create?cat=${encodeURIComponent(createCat)}` : "/create"} data-testid="feed-empty-category-post"
                      className="inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-6 py-3 transition-colors">
                      <Plus className="w-4 h-4" /> Post the First Listing
                    </Link>
                    <button onClick={notifyCategory} data-testid="feed-empty-category-notify"
                      className="inline-flex items-center justify-center gap-2 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-sm px-5 py-2.5 transition-colors">
                      <Bell className="w-4 h-4" /> Notify Me When {tabLabel} Gets Listings
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border border-sniper bg-surface p-16 text-center" data-testid="feed-empty">
                  <p className="font-heading text-2xl uppercase text-secondary-foreground">No optics match</p>
                  <p className="text-sm text-muted-foreground mt-2 font-body">Nothing matches these filters. Widen your search or clear them.</p>
                  <button onClick={reset} data-testid="feed-empty-clear"
                    className="mt-5 inline-flex items-center gap-2 border border-olive/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-sm px-5 py-2.5 transition-colors">
                    <X className="w-4 h-4" /> Clear filters
                  </button>
                </div>
              )
            ) : (
              <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
                {listings.map((l, i) => <ListingCard key={l.listing_id} listing={l} index={i} />)}
              </div>
            )}
          </main>
        </div>
        </>
        )}

        {saveOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" data-testid="save-search-modal" onClick={() => setSaveOpen(false)}>
            <div className="w-full max-w-sm border border-sniper bg-surface p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-heading text-xl uppercase text-foreground flex items-center gap-2"><BookmarkPlus className="w-4 h-4 text-olive-bright" /> Save search</h3>
              <p className="text-xs font-body text-secondary-foreground mt-1">
                Saves your current filters — one click to run it again anytime.
                {!savedMeta.unlimited && ` ${savedMeta.count}/${savedMeta.limit} used.`}
              </p>
              <input autoFocus value={saveName} onChange={(e) => setSaveName(e.target.value)} data-testid="save-search-name"
                placeholder="e.g. 34mm FFP under $1500"
                onKeyDown={(e) => e.key === "Enter" && saveCurrent()}
                className="w-full mt-3 bg-input border border-sniper focus:border-olive outline-none px-3 py-2.5 text-foreground font-body transition-colors" />
              <div className="flex gap-2 mt-4">
                <button onClick={saveCurrent} data-testid="save-search-confirm" className="flex-1 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase py-2.5 transition-colors">Save</button>
                <button onClick={() => setSaveOpen(false)} className="px-4 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-sm transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyerPassModal />
    </div>
  );
}
